import { type ReactNode } from "react";
import { ChevronDown, Hotel, Loader2 } from "lucide-react";
import { Property } from "./FetchProperties";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownFooter,
  PropertyListContent,
  SearchField,
} from "./PropertyListRows";

interface MultiSelectDesktopDropdownProps {
  loading: boolean;
  triggerLabel: string | null;
  dropdownOpen: boolean;
  onOpenChange: (open: boolean) => void;
  dropdownSearch: string;
  onSearchChange: (value: string) => void;
  filteredPortfolio: Property[];
  filteredNonPortfolio: Property[];
  showSelectAllPortfolio: boolean;
  pendingCount: number;
  isChecked: (property: Property) => boolean;
  onToggleProperty: (property: Property) => void;
  onSelectAllPortfolio: () => void;
  onClearPending: () => void;
  onApply: () => void;
  selectionLimitWarning: ReactNode;
}

const MultiSelectDesktopDropdown = ({
  loading,
  triggerLabel,
  dropdownOpen,
  onOpenChange,
  dropdownSearch,
  onSearchChange,
  filteredPortfolio,
  filteredNonPortfolio,
  showSelectAllPortfolio,
  pendingCount,
  isChecked,
  onToggleProperty,
  onSelectAllPortfolio,
  onClearPending,
  onApply,
  selectionLimitWarning,
}: MultiSelectDesktopDropdownProps) => {
  return (
    <Popover open={dropdownOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-10 w-full min-w-0 items-center justify-between gap-1 rounded-[12px] border bg-white px-[6px] shadow-card transition-all duration-150 focus-visible:outline-none hover:shadow-cardHover ${
            dropdownOpen
              ? "border-primary ring-4 ring-primary/15"
              : "border-slate-200 hover:border-slate-300 focus-visible:border-primary focus-visible:ring-4 focus-visible:ring-primary/15"
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {loading ? (
              <Loader2
                size={14}
                className="shrink-0 animate-spin text-slate-400"
              />
            ) : (
              <Hotel size={15} className="shrink-0 text-slate-400" />
            )}
            {triggerLabel ? (
              <span className="inline-flex min-w-0 items-center gap-1.5 overflow-hidden rounded-full bg-primary-light px-2 py-0.5 text-[0.8125rem] font-semibold text-primary">
                <span className="h-1 w-1 shrink-0 rounded-full bg-primary" />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {triggerLabel}
                </span>
              </span>
            ) : (
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] font-normal text-slate-400">
                Select properties
              </span>
            )}
          </div>
          <ChevronDown
            size={15}
            className={`shrink-0 text-slate-400 transition-transform duration-150 ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="mt-1 flex flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-dialog"
      >
        <SearchField
          value={dropdownSearch}
          onChange={onSearchChange}
          isMobile={false}
          autoFocus
        />

        <div className="max-h-[280px] overflow-y-auto overflow-x-hidden py-0.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb [&::-webkit-scrollbar-track]:bg-transparent">
          <PropertyListContent
            isMobile={false}
            filteredPortfolio={filteredPortfolio}
            filteredNonPortfolio={filteredNonPortfolio}
            showSelectAllPortfolio={showSelectAllPortfolio}
            isChecked={isChecked}
            onToggleProperty={onToggleProperty}
            onSelectAllPortfolio={onSelectAllPortfolio}
          />
        </div>

        {selectionLimitWarning}
        <DropdownFooter
          pendingCount={pendingCount}
          onClear={onClearPending}
          onApply={onApply}
          isMobile={false}
        />
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectDesktopDropdown;
